/**
 * collection-availability-sort.js
 *
 * Re-orders a collection grid so that IN-STOCK products come first and
 * SOLD-OUT products come last — across the ENTIRE collection, not per page.
 *
 * Why this exists:
 *  - Liquid paginates the collection before the theme can see it, and the
 *    `paginate` tag is capped at 250 items per page. So a collection of up to
 *    ~1000 products always spans several pages, and Liquid cannot move an
 *    in-stock product from page 3 ahead of a sold-out product on page 1.
 *  - This script fetches every native page (rendered server-side, so the cards
 *    are the REAL Dawn cards with all badges), builds one global order
 *    (available first, sold-out last, original order preserved within each
 *    group) and then renders numbered pages of N products client-side.
 *
 * The server keeps rendering a valid grid, so crawlers / no-JS users still get
 * a working page. This script is purely a progressive enhancement.
 *
 * Activation: only on collections that opt in via [data-availability-sort] and
 * that are NOT handled by the >5000 large-collection Storefront pipeline.
 */

(function () {
  'use strict';

  const SS_PREFIX = 'availSort:'; // sessionStorage namespace
  let instance = null; // the single active sorter for this page

  class CollectionAvailabilitySort {
    constructor(grid) {
      this.grid = grid; // the #product-grid <ul>
      this.container = document.querySelector('#ProductGridContainer');
      this.renderToken = 0;
      this.isRendering = false;
      this.order = null; // array of { html, available }

      this.run();
      this.observe();
      window.addEventListener('popstate', () => this.run());
    }

    // These can change when facets re-render the grid, so read them live.
    get displayPerPage() {
      return parseInt(this.grid.dataset.displayPerPage, 10) || 24;
    }
    get sectionId() {
      return this.grid.dataset.availSectionId;
    }
    get collectionUrl() {
      return this.grid.dataset.collectionUrl || window.location.pathname;
    }
    get serverPages() {
      return parseInt(this.grid.dataset.serverPages, 10) || 1; // native pages @250
    }

    /** Build a cache key from the current filters/sort (page-independent). */
    signature() {
      const p = new URLSearchParams(window.location.search);
      p.delete('page');
      p.delete('section_id');
      p.sort();
      return SS_PREFIX + window.location.pathname + '?' + p.toString();
    }

    currentPage() {
      const p = parseInt(new URLSearchParams(window.location.search).get('page'), 10);
      return p && p > 0 ? p : 1;
    }

    /** Query string to forward to section-render fetches (filters + sort). */
    forwardParams() {
      const p = new URLSearchParams(window.location.search);
      p.delete('page');
      p.delete('section_id');
      return p;
    }

    async run() {
      // Coalesce overlapping invocations (e.g. rapid facet changes): if a run is
      // already in flight, ask it to run once more when it finishes.
      if (this.running) {
        this.rerunRequested = true;
        return;
      }
      this.running = true;
      try {
        do {
          this.rerunRequested = false;
          await this.runOnce();
        } while (this.rerunRequested);
      } finally {
        this.running = false;
      }
    }

    async runOnce() {
      // Snapshot the FULL server-rendered card set before we mutate the grid,
      // because the immediate pass below rewrites the grid to a single page.
      let domCards = [];
      try {
        domCards = this.readCards(document);
      } catch (e) {
        /* ignore */
      }

      // 1) Immediate pass using the cards already in the DOM. This avoids a flash
      //    of all ~250 server-rendered cards and gives a correct first page right
      //    away. If the script had failed to load, the server grid would just show
      //    in full — nothing is ever hidden by CSS, so there's no broken state.
      try {
        if (domCards.length) {
          this.order = this.partition(domCards);
          this.render(this.currentPage());
        }
      } catch (e) {
        /* non-fatal; the accurate pass below will try again */
      }

      // 2) Accurate pass: build the full cross-page order and re-render.
      try {
        const order = await this.buildOrder(domCards);
        if (order && order.length) {
          this.order = order;
          this.render(this.currentPage());
        }
      } catch (e) {
        console.warn('[availability-sort] accurate pass failed:', e);
        // Leave whatever the immediate pass rendered (or the native grid) in place.
      }
    }

    /** Collect every product card across all native pages, then partition. */
    async buildOrder(domCardsSnapshot) {
      const key = this.signature();

      // Try cache first (filters unchanged within the session).
      try {
        const cached = sessionStorage.getItem(key);
        if (cached) return this.partition(JSON.parse(cached));
      } catch (e) {
        /* sessionStorage may be unavailable; ignore */
      }

      // Single native page (collection ≤ 250): every card was already in the DOM,
      // captured in the snapshot before we mutated the grid — no network needed.
      if (this.serverPages <= 1) {
        const cards = domCardsSnapshot && domCardsSnapshot.length
          ? domCardsSnapshot
          : this.readCards(document);
        try {
          sessionStorage.setItem(key, JSON.stringify(cards));
        } catch (e) {
          /* ignore */
        }
        return this.partition(cards);
      }

      // Fetch every native page (1..N) fresh via the Section Rendering API, so we
      // don't depend on which slice the server happened to put in the DOM.
      const totalPages = this.serverPages;
      const base = this.forwardParams();
      const requests = [];
      for (let n = 1; n <= totalPages; n++) {
        const params = new URLSearchParams(base);
        params.set('section_id', this.sectionId);
        params.set('page', String(n));
        const url = this.collectionUrl + '?' + params.toString();
        requests.push(
          fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
            .then((r) => (r.ok ? r.text() : ''))
            .then((html) => {
              if (!html) return [];
              const doc = new DOMParser().parseFromString(html, 'text/html');
              return this.readCards(doc);
            })
            .catch(() => [])
        );
      }
      const pages = await Promise.all(requests);
      const cards = [];
      pages.forEach((arr) => cards.push(...arr));

      try {
        sessionStorage.setItem(key, JSON.stringify(cards));
      } catch (e) {
        /* quota / disabled; fine */
      }

      return this.partition(cards);
    }

    /** Read product cards from a document's #product-grid into plain data. */
    readCards(doc) {
      const items = doc.querySelectorAll('#product-grid > li.grid__item');
      const out = [];
      items.forEach((li) => {
        // Skip server-side loading placeholders, if any.
        if (li.classList.contains('loading-placeholder')) return;
        const avail = li.dataset.available;
        out.push({
          html: li.innerHTML,
          available: avail === undefined ? true : avail === 'true',
        });
      });
      return out;
    }

    /** Stable partition: in-stock first, sold-out last. */
    partition(cards) {
      const inStock = [];
      const soldOut = [];
      for (const c of cards) (c.available ? inStock : soldOut).push(c);
      return inStock.concat(soldOut);
    }

    /** Render a single display page (displayPerPage items) + pagination. */
    render(page) {
      const total = this.order.length;
      const perPage = this.displayPerPage;
      const pages = Math.max(1, Math.ceil(total / perPage));
      const current = Math.min(Math.max(1, page), pages);

      const start = (current - 1) * perPage;
      const slice = this.order.slice(start, start + perPage);

      this.isRendering = true;

      // Rebuild the grid with this page's cards (real Dawn card markup).
      const liClass = this.grid.dataset.itemClass || 'grid__item';
      this.grid.innerHTML = slice
        .map((c) => `<li class="${liClass}">${c.html}</li>`)
        .join('');

      this.renderPagination(current, pages);

      this.renderToken += 1;
      this.grid.setAttribute('data-availability-rendered', String(this.renderToken));
      this.grid.classList.add('availability-sorted');

      // Let custom elements (quick-add, etc.) upgrade, then release the lock.
      requestAnimationFrame(() => {
        this.isRendering = false;
      });
    }

    renderPagination(current, pages) {
      let wrapper = this.container.querySelector('.pagination-wrapper');
      if (pages <= 1) {
        if (wrapper) wrapper.style.display = 'none';
        return;
      }
      if (!wrapper) {
        wrapper = document.createElement('div');
        wrapper.className = 'pagination-wrapper';
        const collection = this.container.querySelector('.collection') || this.container;
        collection.appendChild(wrapper);
      }
      wrapper.style.display = '';

      const params = this.forwardParams();
      const href = (n) => {
        const p = new URLSearchParams(params);
        p.set('page', String(n));
        return this.collectionUrl + '?' + p.toString();
      };

      const parts = this.pageParts(current, pages);
      const caret =
        '<svg aria-hidden="true" focusable="false" class="icon icon-caret" viewBox="0 0 10 6"><path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"></path></svg>';

      let html = '<nav class="pagination" role="navigation" aria-label="עמודים"><ul class="pagination__list list-unstyled" role="list">';

      if (current > 1) {
        html += `<li><a href="${href(current - 1)}" class="pagination__item pagination__item--next pagination__item-arrow link motion-reduce" data-avail-page="${current - 1}" aria-label="עמוד קודם">${caret}</a></li>`;
      }
      for (const part of parts) {
        if (part === '…') {
          html += '<li><span class="pagination__item">…</span></li>';
        } else if (part === current) {
          html += `<li><a role="link" aria-disabled="true" class="pagination__item pagination__item--current light" aria-current="page" aria-label="עמוד ${part}">${part}</a></li>`;
        } else {
          html += `<li><a href="${href(part)}" class="pagination__item link" data-avail-page="${part}" aria-label="עמוד ${part}">${part}</a></li>`;
        }
      }
      if (current < pages) {
        html += `<li><a href="${href(current + 1)}" class="pagination__item pagination__item--prev pagination__item-arrow link motion-reduce" data-avail-page="${current + 1}" aria-label="עמוד הבא">${caret}</a></li>`;
      }
      html += '</ul></nav>';

      wrapper.innerHTML = html;
    }

    /** Windowed page list: 1 … (c-1) c (c+1) … last */
    pageParts(current, pages) {
      const parts = [];
      const add = (n) => parts.push(n);
      const window = 1;
      const left = Math.max(2, current - window);
      const right = Math.min(pages - 1, current + window);
      add(1);
      if (left > 2) parts.push('…');
      for (let i = left; i <= right; i++) add(i);
      if (right < pages - 1) parts.push('…');
      if (pages > 1) add(pages);
      return parts;
    }

    /** Watch for native re-renders (facet filtering) and re-apply ordering. */
    observe() {
      if (!this.container) return;
      this.mo = new MutationObserver(() => {
        if (this.isRendering) return;
        const grid = this.container.querySelector('#product-grid');
        if (!grid) return;
        // Native code replaced the grid (our marker is gone) → re-bind & re-run.
        if (!grid.hasAttribute('data-availability-rendered')) {
          this.grid = grid;
          // Filters likely changed; invalidate nothing here — signature() keys it.
          this.run();
        }
      });
      this.mo.observe(this.container, { childList: true, subtree: true });
    }
  }

  // Intercept clicks on our pagination links: paginate without a full reload,
  // and stop Dawn's facet/pagination handlers from also acting on them.
  document.addEventListener(
    'click',
    function (e) {
      const link = e.target.closest('a[data-avail-page]');
      if (!link || !instance || !instance.order) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      const page = parseInt(link.dataset.availPage, 10) || 1;
      history.pushState({}, '', link.getAttribute('href'));
      instance.render(page);
      const top = document.querySelector('#ProductGridContainer');
      if (top) top.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    true // capture, so we run before Dawn's delegated handlers
  );

  function init() {
    const grid = document.querySelector('#product-grid[data-availability-sort]');
    if (!grid) return;
    // Don't fight the >5000 large-collection Storefront pipeline.
    if (document.querySelector('[data-collection-meta]')) return;
    if (instance) return;
    instance = new CollectionAvailabilitySort(grid);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
